import { useState, useEffect, useMemo } from 'react';
import { Patient } from '../types/patient';
import { Appointment, Transcript } from '../types/appointment';
import { patientService } from '../services/patient';
import { appointmentService } from '../services/appointment';
import { ageInYears } from '../utils/dateFormat';

/**
 * Custom hook for managing patient data in the cockpit view
 * Provides patient information with loading states and error handling
 */
export const usePatientData = (patientId: string) => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPatientData = async () => {
    if (!patientId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await patientService.getPatient(patientId);
      setPatient(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load patient';
      setError(errorMessage);
      console.error('Patient data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientData();
  }, [patientId]);

  return { 
    patient, 
    loading, 
    error, 
    refetch: fetchPatientData 
  };
};

/**
 * Custom hook for managing patient appointments with filtering and sorting
 * Optimized for cockpit view with recent appointments focus
 */
interface UsePatientAppointmentsOptions {
  limit?: number;
  sortBy?: 'scheduledDate' | 'actualDate' | 'createdAt';
  order?: 'asc' | 'desc';
  includeTranscripts?: boolean;
}

export const usePatientAppointments = (
  patientId: string,
  options: UsePatientAppointmentsOptions = {}
) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    limit = 10,
    sortBy = 'scheduledDate',
    order = 'desc',
    includeTranscripts = false
  } = options;

  const fetchAppointments = async () => {
    if (!patientId) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await appointmentService.getPatientAppointments(patientId, {
        limit,
        page: 1
      });
      
      // Sort appointments based on options
      const sortedAppointments = response.appointments.sort((a, b) => {
        const dateA = new Date(a.appointmentDetails[sortBy as keyof typeof a.appointmentDetails] || a.createdAt);
        const dateB = new Date(b.appointmentDetails[sortBy as keyof typeof b.appointmentDetails] || b.createdAt);
        
        return order === 'desc' 
          ? dateB.getTime() - dateA.getTime()
          : dateA.getTime() - dateB.getTime();
      });
      
      setAppointments(sortedAppointments);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load appointments';
      setError(errorMessage);
      console.error('Patient appointments fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [patientId, limit, sortBy, order, includeTranscripts]);

  // Computed values for cockpit display
  const appointmentStats = useMemo(() => {
    const total = appointments.length;
    const completed = appointments.filter(apt => apt.recording.status === 'completed').length;
    const withTranscripts = appointments.filter(apt => apt.transcription.status === 'completed').length;
    const pending = appointments.filter(apt => apt.recording.status === 'pending').length;

    return {
      total,
      completed,
      withTranscripts,
      pending,
      transcriptionRate: total > 0 ? Math.round((withTranscripts / total) * 100) : 0
    };
  }, [appointments]);

  return {
    appointments,
    loading,
    error,
    appointmentStats,
    refetch: fetchAppointments
  };
};

/**
 * Custom hook for clinical insights extracted from patient's appointment history
 * Provides AI-powered analysis of transcripts and clinical trends
 */
export const useClinicalInsights = (appointments: Appointment[]) => {
  return useMemo(() => {
    const completedAppointments = appointments.filter(
      apt => apt.transcription.status === 'completed'
    );

    // Extract chief complaints from recent appointments
    const chiefComplaints = completedAppointments
      .map(apt => apt.appointmentDetails.chiefComplaint)
      .filter(Boolean)
      .slice(0, 5); // Last 5 chief complaints

    // Analyze appointment types frequency
    const appointmentTypes = completedAppointments.reduce((acc, apt) => {
      const type = apt.appointmentDetails.type;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Calculate average visit frequency (days between appointments)
    let averageVisitInterval = 0;
    if (completedAppointments.length > 1) {
      const intervals = [];
      for (let i = 1; i < completedAppointments.length; i++) {
        const current = new Date(completedAppointments[i].appointmentDetails.scheduledDate);
        const previous = new Date(completedAppointments[i-1].appointmentDetails.scheduledDate);
        const diffDays = Math.abs(current.getTime() - previous.getTime()) / (1000 * 60 * 60 * 24);
        intervals.push(diffDays);
      }
      averageVisitInterval = Math.round(intervals.reduce((a, b) => a + b, 0) / intervals.length);
    }

    // Recent visit trends
    const last30Days = completedAppointments.filter(apt => {
      const appointmentDate = new Date(apt.appointmentDetails.scheduledDate);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return appointmentDate >= thirtyDaysAgo;
    }).length;

    return {
      totalVisits: completedAppointments.length,
      recentChiefComplaints: chiefComplaints,
      appointmentTypeBreakdown: appointmentTypes,
      averageVisitInterval,
      visitsLast30Days: last30Days,
      lastVisitDate: completedAppointments[0]?.appointmentDetails.scheduledDate,
      hasRecentActivity: last30Days > 0
    };
  }, [appointments]);
};

/**
 * Custom hook for medical alerts and critical information
 * Analyzes patient data for high-priority clinical alerts
 */
export const useMedicalAlerts = (patient: Patient | null) => {
  return useMemo(() => {
    if (!patient) return { alerts: [], criticalCount: 0 };

    const alerts = [];

    // Critical allergies (common severe allergens)
    const criticalAllergens = ['penicillin', 'latex', 'nuts', 'shellfish', 'eggs'];
    const criticalAllergies = patient.medicalRecord.allergies?.filter(allergy =>
      criticalAllergens.some(critical => 
        allergy.toLowerCase().includes(critical.toLowerCase())
      )
    ) || [];

    if (criticalAllergies.length > 0) {
      alerts.push({
        type: 'critical' as const,
        category: 'allergy',
        message: `Critical allergies: ${criticalAllergies.join(', ')}`,
        icon: '⚠️',
        color: 'red'
      });
    }

    // High-risk conditions
    const highRiskConditions = ['diabetes', 'hypertension', 'heart disease', 'copd', 'asthma'];
    const criticalConditions = patient.medicalRecord.conditions?.filter(condition =>
      highRiskConditions.some(risk =>
        condition.toLowerCase().includes(risk.toLowerCase())
      )
    ) || [];

    if (criticalConditions.length > 0) {
      alerts.push({
        type: 'warning' as const,
        category: 'condition',
        message: `High-risk conditions: ${criticalConditions.slice(0, 2).join(', ')}`,
        icon: '🏥',
        color: 'orange'
      });
    }

    // Medication interactions (simplified check)
    const medicationCount = patient.medicalRecord.medications?.length || 0;
    if (medicationCount > 5) {
      alerts.push({
        type: 'info' as const,
        category: 'medication',
        message: `Multiple medications (${medicationCount}) - check interactions`,
        icon: '💊',
        color: 'blue'
      });
    }

    // Age-based alerts
    const age = ageInYears(patient.demographics.dateOfBirth) ?? 0;
      
    if (age >= 65) {
      alerts.push({
        type: 'info' as const,
        category: 'demographic',
        message: 'Senior patient - consider fall risk and medication sensitivity',
        icon: '👴',
        color: 'purple'
      });
    }

    const criticalCount = alerts.filter(alert => alert.type === 'critical').length;

    return {
      alerts,
      criticalCount,
      hasAlerts: alerts.length > 0
    };
  }, [patient]);
};

/**
 * Composite hook that combines all patient cockpit data
 * Provides a single interface for all cockpit components
 */
export const usePatientCockpitData = (patientId: string) => {
  const patientData = usePatientData(patientId);
  const appointmentsData = usePatientAppointments(patientId, { 
    limit: 5, 
    includeTranscripts: true 
  });
  const clinicalInsights = useClinicalInsights(appointmentsData.appointments);
  const medicalAlerts = useMedicalAlerts(patientData.patient);

  const isLoading = patientData.loading || appointmentsData.loading;
  const hasError = patientData.error || appointmentsData.error;

  return {
    // Patient data
    patient: patientData.patient,
    patientLoading: patientData.loading,
    patientError: patientData.error,
    refetchPatient: patientData.refetch,

    // Appointments data
    appointments: appointmentsData.appointments,
    appointmentsLoading: appointmentsData.loading,
    appointmentsError: appointmentsData.error,
    appointmentStats: appointmentsData.appointmentStats,
    refetchAppointments: appointmentsData.refetch,

    // Clinical insights
    clinicalInsights,
    medicalAlerts,

    // Composite states
    isLoading,
    hasError: !!hasError,
    errorMessage: hasError
  };
};